import apiClient from "./api";

/**
 * Scraper Service
 * Handles all API calls related to service catalog scraping
 * Now uses axios with automatic token refresh
 */
class ScraperService {
  /**
   * Handle axios error
   */
  handleError(error) {
    console.error("Scraper service error:", error);

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      if (status === 401) {
        throw new Error(
          "Your session has expired. Please log in again to continue."
        );
      }

      if (status === 403) {
        throw new Error(
          "You don't have permission to access the service catalog."
        );
      }

      if (status === 404) {
        throw new Error("The requested resource was not found.");
      }

      if (status >= 500) {
        throw new Error(
          "The server is experiencing issues. Please try again later."
        );
      }

      throw new Error(data.message || "An error occurred");
    } else if (error.request) {
      throw new Error(
        "Cannot connect to the server. Please ensure the backend is running."
      );
    } else {
      throw new Error(error.message || "An error occurred");
    }
  }

  /**
   * Fetch service catalog data
   * @returns {Promise<Object>} Service catalog data with apps, plugins, and APIs
   */
  async fetchServiceCatalog() {
    try {
      const response = await apiClient.get("/api/ServiceCatalog");

      const data = response.data;

      if (data.success) {
        return {
          data: data.data,
          metadata: data.metadata,
        };
      }

      throw new Error(data.message || "Failed to fetch service catalog");
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Trigger a fresh scrape of service catalog data
   * @returns {Promise<Object>} Newly scraped data
   */
  async triggerScrape() {
    try {
      const response = await apiClient.post("/api/scraper");

      const data = response.data;

      if (data.success) {
        return {
          data: data.data,
          metadata: data.metadata,
        };
      }

      throw new Error(data.message || "Failed to trigger scrape");
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export a singleton instance
const scraperService = new ScraperService();
export default scraperService;
